import React, { useState, useEffect, useMemo } from "react";
import { Link, useNavigate } from "react-router-dom";
import Swal from "sweetalert2";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faBell,
  faPencil,
  faPlus,
  faTrash,
  faChevronLeft,
  faChevronRight,
  faCode,
  faSort,
  faSortUp,
  faSortDown,
} from "@fortawesome/free-solid-svg-icons";
import axios from "axios";
import { API_URL } from "../../config";
import LoadingSpinner from "../../components/LoadingSpinner";
import { PermissionWrapper, hasPermission } from "../../helpers/permission.js";

const TemplateEmailList = () => {
  const [tempEmail, setTempEmail] = useState([]);
  const [loading, setLoading] = useState(false);
  const [filterLoading, setFilterLoading] = useState(false);
  const [error, setError] = useState(null);
  const token = localStorage.getItem("authToken");
  const [senderList, setSenderList] = useState([]);

  // Search filter states
  const [templateNameFilter, setTemplateNameFilter] = useState("");
  const [subjectFilter, setSubjectFilter] = useState("");
  const [senderTypeFilter, setSenderTypeFilter] = useState("");

  const [sortField, setSortField] = useState("");
  const [sortOrder, setSortOrder] = useState("");

  const sortBox = (field) => {
    if (sortField === field) {
      const newOrder = sortOrder === "asc" ? "desc" : "asc";
      setSortOrder(newOrder);
    } else {
      setSortField(field);
      setSortOrder("asc");
    }
  };

  const getSortIcon = (field) => {
    if (sortField === field) {
      return sortOrder === "asc" ? faSortUp : faSortDown;
    }
    return faSort;
  };

  // 📑 Pagination
  const [page, setPage] = useState(1);
  const [pageCount, setPageCount] = useState(1);
  const [total, setTotal] = useState(0);
  const limit = 100;

  const navigate = useNavigate();

  const fetchSenderList = async () => {
    try {
      const response = await axios.get(
        `${API_URL}settings/emailconfig/dropdown`,
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );

      const senderOptions = response.data?.data?.email?.map((item) => ({
        value: Number(item.value),
        label: item.label,
        sender: item.sender, // include sender here
      }));

      setSenderList(senderOptions || []);
    } catch (err) {
      console.error("Error fetching sender list", err);
    }
  };

  const fetchtempEmail = async () => {
    try {
      setLoading(true);
      const response = await axios.get(
        `${API_URL}settings/template/email/list`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
          params: senderTypeFilter ? { sender_type: senderTypeFilter } : {},
        }
      );
      // An empty list comes back with status 404 in the body and no data
      setTempEmail(Array.isArray(response.data?.data) ? response.data.data : []);
    } catch (error) {
      if (error.response?.status === 404) {
        setTempEmail([]);
      } else {
        setError(error.message);
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchSenderList();
  }, []);

  useEffect(() => {
    fetchtempEmail();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [senderTypeFilter]);

  // Filter templates based on search criteria
  const filteredTemplates = useMemo(() => {
    let result = tempEmail.filter((template) => {
      if (
        templateNameFilter &&
        !template.templatename
          .toLowerCase()
          .includes(templateNameFilter.toLowerCase())
      ) {
        return false;
      }
      if (
        subjectFilter &&
        !template.subject.toLowerCase().includes(subjectFilter.toLowerCase())
      ) {
        return false;
      }
      return true;
    });

    if (sortField) {
      result = [...result].sort((a, b) => {
        let valA = a[sortField];
        let valB = b[sortField];

        if (sortField === "sender_type") {
            valA = getSenderLabel(valA);
            valB = getSenderLabel(valB);
        }

        if (valA == null) valA = "";
        if (valB == null) valB = "";

        if (valA === valB) return 0;
        
        // Handle numbers
        if (typeof valA === 'number' && typeof valB === 'number') {
            return sortOrder === "asc" ? valA - valB : valB - valA;
        }

        // Handle strings that could be numbers
        const numA = Number(valA);
        const numB = Number(valB);
        if (!isNaN(numA) && !isNaN(numB) && valA !== "" && valB !== "") {
            return sortOrder === "asc" ? numA - numB : numB - numA;
        }

        // String comparison
        const strA = String(valA).toLowerCase();
        const strB = String(valB).toLowerCase();
        
        if (strA < strB) return sortOrder === "asc" ? -1 : 1;
        if (strA > strB) return sortOrder === "asc" ? 1 : -1;
        return 0;
      });
    }

    return result;
  }, [tempEmail, templateNameFilter, subjectFilter, sortField, sortOrder, senderList]);

  useEffect(() => {
    if (!tempEmail.length) return;

    setFilterLoading(true);
    const timer = setTimeout(() => {
      setFilterLoading(false);
    }, 300);

    return () => clearTimeout(timer);
  }, [templateNameFilter, subjectFilter, tempEmail]);


  // 📑 Update pagination when filters or data change
  useEffect(() => {
    setTotal(filteredTemplates.length);
    setPageCount(Math.ceil(filteredTemplates.length / limit));
    setPage(1);
  }, [filteredTemplates, limit]);

  const handlePageChange = (newPage) => {
    if (newPage >= 1 && newPage <= pageCount) {
      setPage(newPage);
    }
  };

  const start = (page - 1) * limit + 1;
  const end = Math.min(page * limit, total);
  const paginatedTemplates = filteredTemplates.slice(start - 1, end);

  // Delete Confirmation
  const handleDelete = async (id) => {
    const result = await Swal.fire({
      title: "Are you sure?",
      text: "You will not be able to recover this email template!",
      icon: "warning",
      showCancelButton: true,
      confirmButtonClass: "btn-danger",
      confirmButtonText: "Yes, delete it!",
      cancelButtonText: "No, cancel!",
    });

    if (result.isConfirmed) {
      try {
        await axios.delete(`${API_URL}settings/template/email/delete?${id}`, {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        });
        setTempEmail((prev) => prev.filter((template) => template.id !== id));
        Swal.fire(
          "Deleted!",
          "Your email template has been deleted.",
          "success"
        );
      } catch (error) {
        Swal.fire(
          "Error",
          error.response?.data?.message ||
          "Something went wrong while deleting the template.",
          "error"
        );
      }
    }
  };

  // 🔔 Notification Settings Test API Call
  const handleNotificationTest = async (id) => {
    try {
      setLoading(true);
      const response = await axios.post(
        `${API_URL}settings/template/email/testEmail`,
        { id },
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        }
      );

      const data = response.data;
      const msg =
        data.message ||
        `<strong>Subscriber</strong> => ${data.subMsg}<br><strong>User</strong> => ${data.userMsg}`;

      Swal.fire({
        title: "Test Response",
        html: msg,
        icon: "info",
      });
    } catch (error) {
      Swal.fire(
        "Error",
        error.response?.data?.message ||
        "Something went wrong while testing notification.",
        "error"
      );
    } finally {
      setLoading(false);
    }
  };

  // Helper to map sender_type to label and sender
  // Used memoized useCallback or simply standard function since it's only dependent on senderList
  const getSenderLabel = (senderType) => {
    const sender = senderList.find((s) => s.value === senderType);
    return sender
      ? `${sender.label} (${sender.sender || "-"})`
      : senderType || "-";
  };

  useEffect(() => {
    const handleClearFilters = () => {
      setTemplateNameFilter("");
      setSubjectFilter("");
      setSenderTypeFilter("");
    };
    window.addEventListener('clearTemplateEmailFilterEvent', handleClearFilters);

    return () => {
      window.removeEventListener('clearTemplateEmailFilterEvent', handleClearFilters);
    };
  }, []);

  return (
    <div
      id="page-container"
      className="page-header-fixed page-sidebar-fixed fade in"
    >
      <div id="content" className="">
        <div className="row pb-2">
          <div className="col-md-6">
            <h4 className="panel-title pull-left">Manage Template Email</h4>
          </div>
          <div className="col-md-6" style={{ textAlign: "right" }}>
            <PermissionWrapper module="settings_email" action="add">
              <Link
                to={"/Settings/TemplateEmail/add"}
                className="btn btn-default btn-sm"
              >
                <FontAwesomeIcon icon={faPlus} /> Add Email Template
              </Link>
            </PermissionWrapper>
            {filteredTemplates.length > 0 && (
              <div className="d-inline-block ms-3">
                <span style={{ fontSize: "0.8rem" }} className="me-2">
                  {start} to {end} of {total}
                </span>
                <button
                  className="btn btn-default btn-sm"
                  title="Previous"
                  onClick={() => handlePageChange(page - 1)}
                  disabled={page === 1}
                >
                  <FontAwesomeIcon icon={faChevronLeft} />
                </button>
                &nbsp;
                <button
                  className="btn btn-default btn-sm"
                  title="Next"
                  onClick={() => handlePageChange(page + 1)}
                  disabled={page === pageCount}
                >
                  <FontAwesomeIcon icon={faChevronRight} />
                </button>
              </div>
            )}
          </div>
        </div>

        <div className="panel panel-default with-rounded-corner with-shadow">
          {loading || tempEmail.length > 0 || senderTypeFilter ? (
            <>
              <div className="panel-heading border-0 d-flex justify-content-between align-items-center">
                <div></div>
              </div>
              <style>
                {`
                  .sticky-email-table thead tr:first-child th {
                    position: sticky;
                    top: 0;
                    z-index: 20;
                    background: #f8f9fa;
            
                    }
          
                  .sticky-email-table thead tr:nth-child(2) th {
                    position: sticky;
                    top: 30px; /* Adjust based on your first header row height */
                    z-index: 19;
                    background: white;
            
                    }
          
                  .sticky-table-container {
                    max-height: calc(100vh - 190px);
                    overflow: auto;
                    }
          
                  .sticky-email-table th {
                    vertical-align: top;
                    }
                `}
              </style>
              <div className="table-responsive sticky-table-container">
                <table className="table m-b-0 sticky-email-table">
                  <thead>
                    <tr>
                      <th style={{ textAlign: "center" }}>#</th>
                      <th style={{ cursor: "pointer", whiteSpace: "nowrap" }} onClick={() => sortBox("templatename")}>Template Name <FontAwesomeIcon icon={getSortIcon("templatename")} /></th>
                      <th style={{ cursor: "pointer", whiteSpace: "nowrap" }} onClick={() => sortBox("subject")}>Subject <FontAwesomeIcon icon={getSortIcon("subject")} /></th>
                      <th style={{ cursor: "pointer", whiteSpace: "nowrap" }} onClick={() => sortBox("sender_type")}>Sender Type <FontAwesomeIcon icon={getSortIcon("sender_type")} /></th>
                      <th>Action</th>
                    </tr>
                    <tr>
                      <th style={{ textAlign: "center" }}></th>
                      <th>
                        <div className="input-group input-group-sm ">
                          <input
                            type="text"
                            className="form-control"
                            placeholder="Search template..."
                            value={templateNameFilter}
                            onChange={(e) =>
                              setTemplateNameFilter(e.target.value)
                            }
                          />
                        </div>
                      </th>
                      <th>
                        <div className="input-group input-group-sm">
                          <input
                            type="text"
                            className="form-control"
                            placeholder="Search subject..."
                            value={subjectFilter}
                            onChange={(e) => setSubjectFilter(e.target.value)}
                          />
                        </div>
                      </th>
                      <th>
                        <select
                          style={{ width: "100%", minWidth: "180px", height: "auto", padding: "5px" }}
                          value={senderTypeFilter}
                          onChange={(e) => setSenderTypeFilter(e.target.value)}
                        >
                          <option value="">All</option>
                          {senderList.map((s) => (
                            <option key={s.value} value={s.value}>
                              {s.label} ({s.sender || "-"})
                            </option>
                          ))}
                        </select>
                      </th>
                      <th>
                        <button
                          className="btn btn-default btn-sm"
                          onClick={() => {
                            setTemplateNameFilter("");
                            setSubjectFilter("");
                            setSenderTypeFilter("");
                          }}
                          title="Clear filters"
                        >
                          Clear
                        </button>
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {loading || filterLoading ? (
                      Array.from({ length: 10 }).map((_, rowIndex) => (
                        <tr key={rowIndex} className="skeleton-row">
                          {Array.from({ length: 5 }).map((_, colIndex) => (
                            <td key={colIndex}>
                              <div className="skeleton-box"></div>
                            </td>
                          ))}
                        </tr>
                      ))
                    ) : paginatedTemplates.length > 0 ? (
                      paginatedTemplates.map((post, index) => (
                        <tr key={post.id}>
                          <td style={{ textAlign: "center", verticalAlign: "middle" }}>
                            {start + index}
                          </td>
                          <td>{post.templatename}</td>
                          <td>{post.subject}</td>
                          <td>{getSenderLabel(post.sender_type)}</td>
                          <td>
                            <div className="d-flex gap-2">
                              <PermissionWrapper module="settings_email" action="edit">
                                <button
                                  onClick={() =>
                                    navigate(`/Settings/TemplateEmail/edit/${post.id}`)
                                  }
                                  className="btn btn-warning btn-xs"
                                  title="Edit Template"
                                >
                                  <FontAwesomeIcon icon={faPencil} />
                                </button>
                              </PermissionWrapper>
                              <PermissionWrapper module="settings_email" action="edit">
                                <button
                                  onClick={() =>
                                    window.open(`/Settings/TemplateEmail/html-edit/${post.id}`, "_blank")
                                  }
                                  className="btn btn-primary btn-xs"
                                  title="Edit HTML Code"
                                >
                                  <FontAwesomeIcon icon={faCode} />
                                </button>
                              </PermissionWrapper>
                              <button
                                onClick={() => handleNotificationTest(post.id)}
                                className="btn btn-info btn-xs"
                                title="Notification Settings"
                              >
                                <FontAwesomeIcon icon={faBell} />
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))
                    ) : (
                      <tr>
                        <td colSpan="5" className="text-center">
                          No templates match your search criteria.
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </>
          ) : (
            <div className="alert alert-warning">
              No records found.{" "}
              <Link to="/Settings/TemplateEmail/add">Add Email Template</Link>.
            </div>
          )}
        </div>
      </div>

      <a href="#" data-click="scroll-top" className="btn-scroll-top fade">
        <i className="ti-arrow-up"></i>
      </a>
    </div>
  );
};

export default TemplateEmailList;
